import { useCallback, useMemo, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";

import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import { shouldShowNow } from "./daily-window.util";

/** Bump the version suffix to give every user a fresh showing at once. */
const WINDOW_STORAGE_KEY = "suno_qbo_reconnect_banner_v1_";

interface WindowState {
  shownAt?: string;
}

/**
 * Per-user daily window for the QBO reconnect banner.
 *
 * The banner is a once-a-day notice pinned to 2pm Eastern, not a persistent bar: it is invisible
 * before 2pm, `markShown` records the showing as soon as it renders, and it then stays gone until
 * the next day's 2pm. `dismiss` is only an early exit from the current page load — by the time the
 * user can click it the day's showing is already recorded.
 *
 * Deliberately NOT `useDismissibleBanner`: that hook's contract is a one-time announcement that
 * stays dismissed (plus a TTL from first-seen), which is wrong for a recurring operational alert.
 * A broken QuickBooks connection blocks all syncing until somebody reconnects, so hiding it here
 * has to mean "not until tomorrow", not "never again". Adding a second, opposing storage semantic
 * to a hook with a dozen consumers would make both harder to reason about.
 */
export function useQboBannerDailyWindow(userId: string) {
  const storage = useMemo(
    () => setupLocalStorageAPI(`${WINDOW_STORAGE_KEY}${userId}`),
    [userId]
  );

  // Decided once, at mount: whether this page load is the day's showing. Read in an initializer
  // rather than on every render so the answer cannot change under a user mid-read when the clock
  // crosses 2pm.
  const [visible, setVisible] = useState(() => {
    const state = storage.getData(null) as WindowState | null;
    return shouldShowNow(state?.shownAt);
  });

  /**
   * Record the day's showing, without hiding the banner the user is currently looking at — this
   * writes storage but deliberately does not touch `visible`. A banner that vanished on its own
   * mid-read would be worse than one that stays until the next page load.
   */
  const markShown = useCallback(() => {
    storage.updateData({ shownAt: new Date().toISOString() });
  }, [storage]);

  // No storage write: `markShown` already spent the day's showing when the banner rendered.
  const dismiss = useCallback(() => {
    setVisible(false);
    datadogRum.addAction("qbo_reconnect_banner_dismissed", {});
  }, []);

  return { visible, markShown, dismiss };
}
